import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Badge,
  Button,
  Card,
  Input,
  Label,
  Spinner,
  EditableCombobox,
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@agion/ui'
import { Check, ArrowLeft, ArrowRight, Rocket } from 'lucide-react'
import type { CloudEnvironment } from '@/core/infra_types'
import { listCredentials, validateCredential } from '@/features/secondary/infra_credential.service'
import { listSshKeys } from '@/features/secondary/infra_ssh_key.service'
import { listAzureGalleryImages, listImages } from '@/features/secondary/infra_image.service'
import {
  getModuleVersions,
  getTerraformRelease,
  listTerraformModules,
} from '@/features/terraform/infra_terraform.service'
import { createOperation } from '@/features/operation/infra_operation.service'
import { listPendingHosts, type PendingHost } from './infra_governance.service'
import { createEnvironment, listEnvironments } from './infra_environment.service'
import { allPages, useVmLookup } from './useVmLookup'
import {
  appliedDiskSizeGb,
  emptyDiskKeepsCurrent,
  buildVmEnvironment,
  buildVmOperation,
  credentialDestination,
  diskSizeBounds,
  hostProvider,
  includedDiskSizeGb,
  vmImageReference,
  newVmDraft,
  vmDraftErrors,
  type VmDraft,
  type VmDraftError,
} from './vmDeployment'
import { suggestAzureNetwork, validAzureNetwork } from './vmNetwork'
import { AdvancedSettings } from './AdvancedSettings'
import { AzureSshAccess } from './AzureSshAccess'
import { vmDraftFromEnvironment } from './vmDraftFromEnvironment'
import {
  AZURE_VM_SIZES,
  UPCLOUD_PLANS,
  gcpVmSizesForRegion,
  AZURE_DB_SKUS,
  GCP_DB_TIERS,
  UPCLOUD_DB_PLANS,
  canEnableJinaReranker,
  upcloudPlanStorageGb,
} from './infra_vm_sizes'

function Field({
  id,
  label,
  children,
  hint,
}: {
  id: string
  label: string
  children: ReactNode
  hint?: string
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint && <p className="text-sm text-slate-500">{hint}</p>}
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-4 border-t border-slate-200 pt-5 first:border-0 first:pt-0 dark:border-slate-700">
      <h3 className="text-base font-semibold">{title}</h3>
      {children}
    </section>
  )
}

function Choice({
  id,
  value,
  onChange,
  children,
}: {
  id: string
  value: string
  onChange: (value: string) => void
  children: ReactNode
}) {
  return (
    <Select value={value || '__none'} onValueChange={(v) => onChange(v === '__none' ? '' : v)}>
      <SelectTrigger id={id}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>{children}</SelectContent>
    </Select>
  )
}

function Lookup({
  state,
  children,
  errorMessage,
  retryLabel,
}: {
  state: { loading: boolean; error?: string; retry: () => void }
  children: ReactNode
  errorMessage?: string
  retryLabel?: string
}) {
  const { t } = useTranslation('pages')
  if (state.loading)
    return (
      <div role="status" className="flex items-center gap-2 py-3">
        <Spinner />
        {t('vmWizard.loading', 'Loading…')}
      </div>
    )
  if (state.error)
    return (
      <Alert variant="error">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span>
            {errorMessage ?? t(
              'vmWizard.lookupFailed',
              'Could not load this section. Your selections have been kept.'
            )}
          </span>
          <Button variant="outline" onClick={state.retry}>
            {retryLabel ?? t('onboarding.retry', 'Retry')}
          </Button>
        </div>
      </Alert>
    )
  return <>{children}</>
}

interface VmWizardProps {
  organization: { id: string; name: string }
  existingEnvironment?: CloudEnvironment
  onSuccess?: (operationId: string) => void
  onBusyChange?: (busy: boolean) => void
}

/** Canonical single-VM editor for initial provisioning and deliberate reapply. */
export function SingleVmWizard({ organization, existingEnvironment, onSuccess, onBusyChange }: VmWizardProps) {
  const { t } = useTranslation(['pages'])
  const navigate = useNavigate()
  const reapply = !!existingEnvironment
  const [step, setStep] = useState(reapply ? 1 : 0)
  const [host, setHost] = useState<PendingHost>()
  const [reopened] = useState(() => existingEnvironment ? vmDraftFromEnvironment(existingEnvironment) : undefined)
  const [draft, setDraft] = useState<VmDraft | undefined>(reopened)
  const appliedDisk = existingEnvironment ? appliedDiskSizeGb(existingEnvironment) : undefined
  // A new UpCloud VM follows the plan's included storage until the operator sets a size.
  const diskChosen = useRef(false)
  const [environment, setEnvironment] = useState<CloudEnvironment>()
  const [validatedCredential, setValidatedCredential] = useState('')
  const [validating, setValidating] = useState(false)
  const [validationError, setValidationError] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()
  const [advanced, setAdvanced] = useState(false)
  const submitting = useRef(false)
  const [submissionStarted, setSubmissionStarted] = useState(false)
  const locked = reapply ? submissionStarted : !!environment
  const targetName = existingEnvironment?.name ?? host?.name
  const heading = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    heading.current?.focus()
  }, [step])
  const targets = useVmLookup(
    useCallback(async () => {
      if (reapply) return { hosts: [], environments: [] }
      const [hosts, environments] = await Promise.all([
        listPendingHosts(),
        allPages((page) => listEnvironments({ page, perPage: 100 })),
      ])
      return { hosts: hosts.filter((h) => hostProvider(h)), environments }
    }, [reapply])
  )

  const selectHost = (selected: PendingHost, existing?: CloudEnvironment) => {
    if (busy || environment) return
    const fresh = existing ? vmDraftFromEnvironment(existing,
      String(existing.config['requestId'] ?? existing.config['request_id'] ?? ''), 'resume') : newVmDraft(selected)
    setHost(selected)
    setDraft(fresh)
    setEnvironment(existing)
    setValidatedCredential('')
    setValidationError(false)
    setError(undefined)
    setAdvanced(false)
    diskChosen.current = !!existing
  }

  const provider = draft?.provider
  const credentials = useVmLookup(
    useCallback(
      () =>
        provider
          ? allPages((page) =>
              listCredentials({ cloudProvider: provider, status: 'active', page, perPage: 100 })
            )
          : Promise.resolve([]),
      [provider]
    )
  )
  const imageCredentialId = draft?.credentialId
  const imageRegion = draft?.region ?? ''
  const images = useVmLookup(
    useCallback(
      async () => {
        if (provider === 'azure') {
          if (!imageCredentialId || !imageRegion) return { images: [], gallery: undefined }
          const catalog = await listAzureGalleryImages(imageCredentialId, imageRegion)
          return { ...catalog, images: catalog.images.map((image) => ({ ...image, imageId: image.id })) }
        }
        return {
          images: provider ? await allPages((page) => listImages({ cloudProvider: provider, status: 'available', page, perPage: 100 })) : [],
          gallery: undefined,
        }
      },
      [provider, imageCredentialId, imageRegion]
    )
  )
  const keys = useVmLookup(
    useCallback(() => allPages((page) => listSshKeys({ page, perPage: 100 })), [])
  )
  const modules = useVmLookup(useCallback(listTerraformModules, []))
  const credential = credentials.data?.find(
    (c) => c.id === draft?.credentialId && c.cloudProvider === provider
  )
  const destination = credentialDestination(credential)
  const versions = getModuleVersions(modules.data ?? [], `${provider}-vm`)
  const selectedModuleVersion = draft?.moduleVersion
  const releaseLookup = useVmLookup(
    useCallback(
      () =>
        provider && selectedModuleVersion
          ? getTerraformRelease(`${provider}-vm`, selectedModuleVersion)
          : Promise.resolve(undefined),
      [provider, selectedModuleVersion]
    )
  )
  const release = releaseLookup.data
  useEffect(() => {
    if (!locked && release)
      setDraft((d) =>
        d && d.moduleDigest === release.digest
          ? { ...d, terraformVersion: release.terraformVersion ?? '' }
          : d
      )
  }, [release, locked])
  const image = images.data?.images.find((i) => vmImageReference(provider, i) === draft?.imageName)
  const patch = <K extends keyof VmDraft>(key: K, value: VmDraft[K]) => {
    if (locked) return // An attempted submission is immutable; resume it instead of changing its intent.
    setDraft((d) => (d ? {
      ...d,
      [key]: value,
      ...(d.provider === 'azure' && (key === 'region' || key === 'credentialId') && d[key] !== value ? { imageName: '' } : {}),
    } : d))
    setError(undefined)
  }
  const chooseVmSize = (vmSize: string) => {
    patch('vmSize', vmSize)
    const included = reapply || diskChosen.current ? '' : includedDiskSizeGb(provider, vmSize)
    if (included) patch('diskSizeGb', included)
  }
  const chooseDiskSize = (size: string) => {
    if (size !== draft?.diskSizeGb) diskChosen.current = true
    patch('diskSizeGb', size)
  }
  const chooseVersion = (version: string) => {
    if (locked) return
    const artifact = modules.data?.find(
      (m) => m.module === `${provider}-vm` && m.tags.includes(version)
    )
    setDraft((d) =>
      d
        ? {
            ...d,
            moduleVersion: version,
            moduleDigest: artifact?.digest ?? '',
            terraformVersion: artifact?.terraformVersion ?? '',
          }
        : d
    )
  }
  const configurationLookups = [
    { state: credentials, label: t('pages:vmWizard.cloudConnection', 'Cloud connection') },
    { state: images, label: t('pages:vmWizard.baseImage', 'VM base image') },
    { state: keys, label: t('pages:vmWizard.access', 'Host access') },
    { state: modules, label: t('pages:vmWizard.release', 'Terraform release') },
    { state: releaseLookup, label: t('pages:onboarding.configure.tfModuleVersion') },
  ]
  const allReady = configurationLookups.map(({ state }) => state).every(
    (s) => !s.loading && !s.error
  )
  const draftErrors = draft ? vmDraftErrors(draft, appliedDisk) : []
  const diskBounds = diskSizeBounds(provider)
  const configurationErrors = new Set(draftErrors)
  if (!image) configurationErrors.add('image')
  if (!release || draft?.moduleDigest !== release.digest) configurationErrors.add('release')
  if (provider === 'azure' && draft?.sshAccessMode !== 'legacy' && release && release.sshAccessPolicy !== 'v1') configurationErrors.add('accessRelease')
  // azure-vm puts the first of its SSH keys in admin_ssh_key, which is
  // ForceNew on azurerm_linux_virtual_machine. infra-service resolves the key
  // from the recorded ssh_key_id, so a re-apply whose key is missing or
  // deleted would destroy the VM, its monitoring extension and its data
  // collection rule association instead of changing what was asked for.
  if (reapply && provider === 'azure' && keys.data && !keys.data.some((key) => key.id === draft?.sshKeyId))
    configurationErrors.add('sshKeyAzure')
  const configValid =
    !!draft &&
    !configurationErrors.size
  const databaseTiers = provider === 'azure' ? AZURE_DB_SKUS : provider === 'gcp' ? GCP_DB_TIERS : UPCLOUD_DB_PLANS
  const configurationErrorMessages: Record<VmDraftError, string> = {
    credential: t('pages:vmWizard.requirements.credential', 'Select a cloud credential in Connection.'),
    compute: t('pages:vmWizard.requirements.compute', 'Enter a valid region and VM size in Compute.'),
    release: t('pages:vmWizard.requirements.release', 'Select a pinned Terraform release and resolve any release errors.'),
    image: t('pages:vmWizard.requirements.image', 'Select an available VM base image for the current connection and region.'),
    network: t('pages:vmWizard.requirements.network', 'Correct the address ranges in Network.'),
    access: t('pages:vmWizard.requirements.access', 'Enter valid allowed SSH source ranges in Host access.'),
    accessRelease: t('pages:sshAccess.releaseRequired'),
    sshKeyAzure: t('pages:vmWizard.requirements.sshKeyAzure', 'The SSH key of this virtual machine cannot be resolved, and applying without it destroys the virtual machine and everything on its disk. Restore the SSH key this machine was built with under Security, SSH keys, then re-apply.'),
    storage: t('pages:vmWizard.requirements.storage', { defaultValue: 'Set the boot disk size to {{min}}–{{max}} GB in Advanced settings, or leave it empty for the default.', ...diskBounds }),
    diskShrink: t('pages:vmWizard.requirements.diskShrink', { defaultValue: 'UpCloud disks cannot shrink. Set the boot disk to at least {{size}} GB in Advanced settings, or leave it empty to keep the current disk.', size: appliedDisk }),
    databaseDisk: t('pages:vmWizard.requirements.databaseDisk'),
    diagnostics: t('pages:vmWizard.requirements.diagnostics', 'Set log retention to 30–730 days in Advanced settings.'),
    database: draft?.dbTier
      ? t('pages:vmWizard.requirements.intelligenceDatabase', 'Choose an Intelligence database tier in Database.')
      : t('pages:vmWizard.requirements.database', 'Choose a managed database tier in Database.'),
  }
  const connectionValid =
    !!credential &&
    (!existingEnvironment?.credentialVaultPath || credential.vaultPath === existingEnvironment.credentialVaultPath) &&
    validatedCredential === credential.id &&
    (provider === 'upcloud' || !!destination)

  const checkCredential = async () => {
    if (!credential || validating) return
    const id = credential.id
    setValidating(true)
    setValidationError(false)
    try {
      const result = await validateCredential(id)
      setValidatedCredential(result.valid ? id : '')
      setValidationError(!result.valid)
    } catch {
      setValidationError(true)
      setValidatedCredential('')
    } finally {
      setValidating(false)
    }
  }

  const submit = async () => {
    if (
      !draft ||
      (!reapply && !host) ||
      !credential ||
      !allReady ||
      !configValid ||
      !connectionValid ||
      submitting.current
    )
      return
    submitting.current = true
    setSubmissionStarted(true)
    setBusy(true)
    onBusyChange?.(true)
    setError(undefined)
    try {
      const env = existingEnvironment ?? environment ?? (await createEnvironment(buildVmEnvironment(draft, host!)))
      setEnvironment(env)
      const operation = await createOperation(buildVmOperation(draft, env, credential, reapply ? 'reapply' : 'create'))
      if (onSuccess) onSuccess(operation.id)
      else navigate(`/operations/${operation.id}`)
    } catch {
      setError(
        t(
          'pages:vmWizard.submitFailed',
          'Submission could not be confirmed. Resume this same submission to recover its existing environment or operation; this does not automatically retry Terraform.'
        )
      )
    } finally {
      submitting.current = false
      setBusy(false)
      onBusyChange?.(false)
    }
  }

  const steps = [
    t('pages:vmWizard.target', 'Target'),
    t('pages:vmWizard.connection', 'Connection'),
    t('pages:vmWizard.configure', 'Configure'),
    t('pages:vmWizard.review', 'Review'),
  ]
  const labels = {
    networkCidr: t('pages:vmWizard.vnet', 'VNet address space'),
    subnetCidr: t('pages:vmWizard.vmSubnet', 'VM subnet'),
    dbSubnetCidr: t('pages:vmWizard.dbSubnet', 'Database subnet reservation'),
    dfsSubnetCidr: t('pages:vmWizard.dfsSubnet', 'Storage private-endpoint subnet reservation'),
  }
  const networkKeys =
    provider === 'azure'
      ? (Object.keys(labels) as (keyof typeof labels)[])
      : (['networkCidr'] as const)
  const releaseLabel = `${provider}-vm ${draft?.moduleVersion ?? ''}`
  const publicSsh = draft?.sshAllowedCidrs
    .split(',')
    .some((v) => ['*', '0.0.0.0/0', '::/0'].includes(v.trim()))
  const nextAllowed = step === 0 ? !!host : step === 1 ? connectionValid : allReady && configValid

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <nav aria-label={t('pages:vmWizard.steps', 'Provisioning steps')}>
        <ol className={`grid grid-cols-2 gap-2 ${reapply ? 'sm:grid-cols-3' : 'sm:grid-cols-4'}`}>
          {steps.map((label, index) => (
            !(reapply && index === 0) &&
            <li
              key={label}
              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${index === step ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}
              aria-current={index === step ? 'step' : undefined}
            >
              <span aria-hidden="true">{index < step ? <Check size={16} /> : index + (reapply ? 0 : 1)}</span>
              {label}
            </li>
          ))}
        </ol>
      </nav>
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
        <Card className="min-w-0">
          <div className="space-y-6 p-5 sm:p-6">
            <h2 ref={heading} tabIndex={-1} className="text-xl font-semibold outline-none">
              {steps[step]}
            </h2>
            {error && <Alert variant="error">{error}</Alert>}
            {locked && (
              <Alert variant="info">
                {t(
                  'pages:vmWizard.saved',
                  'This setup is already saved. Its configuration is locked so that resuming cannot change or duplicate the deployment.'
                )}
              </Alert>
            )}
            {step === 0 && (
              <Lookup state={targets}>
                <p className="text-sm text-slate-500">
                  {t(
                    'pages:vmWizard.targetHint',
                    'Choose a pending host created in Admin UI. Its cloud provider determines the configuration that follows.'
                  )}
                </p>
                {!targets.data?.hosts.length && (
                  <Alert variant="info">{t('pages:onboarding.singleHost.empty.description')}</Alert>
                )}
                <div className="space-y-3">
                  {targets.data?.hosts.map((item) => {
                    const existing = targets.data?.environments.find(
                      (e) => (e.config['hostId'] ?? e.config['host_id']) === item.id
                    )
                    const resumable =
                      existing?.config['onboardingDraft'] ?? existing?.config['onboarding_draft']
                    return (
                      <div key={item.id}>
                        <Button
                          variant="outline"
                          type="button"
                          disabled={
                            busy ||
                            (!!environment && host?.id !== item.id) ||
                            (!!existing && !resumable)
                          }
                          aria-pressed={host?.id === item.id}
                          onClick={() => selectHost(item, existing)}
                          className={`flex h-auto min-h-16 w-full items-center justify-between gap-3 rounded-lg border p-4 text-left disabled:opacity-60 sm:px-5 ${host?.id === item.id ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' : 'border-slate-300 dark:border-slate-700'}`}
                        >
                          <span className="min-w-0 flex-1 truncate font-medium leading-6" title={item.name}>
                            {item.name}
                          </span>
                          <span className="flex shrink-0 items-center gap-2">
                            <Badge variant={resumable ? 'default' : 'warning'} size="sm">
                              {resumable
                                ? t('pages:vmWizard.resumeSetup', 'Resume saved setup')
                                : t('pages:onboarding.singleHost.pending', 'Pending')}
                            </Badge>
                            <Badge variant="info" size="sm" className="uppercase">
                              {hostProvider(item)}
                            </Badge>
                          </span>
                        </Button>
                        {existing && (
                          <Button
                            variant="ghost"
                            onClick={() => navigate(`/environments/${existing.id}`)}
                          >
                            {t('pages:vmWizard.openEnvironment', 'Open existing environment')}
                          </Button>
                        )}
                      </div>
                    )
                  })}
                </div>
                <Button variant="outline" onClick={targets.retry}>
                  {t('pages:vmWizard.refreshHosts', 'Refresh hosts')}
                </Button>
              </Lookup>
            )}
            {draft && step === 1 && (
              <Section title={t('pages:vmWizard.cloudConnection', 'Cloud connection')}>
                {reapply && !credentials.loading && !credentials.error && (!credential || (existingEnvironment.credentialVaultPath && credential.vaultPath !== existingEnvironment.credentialVaultPath)) && (
                  <Alert variant="error">{t('pages:vmWizard.boundCredentialUnavailable')}</Alert>
                )}
                <Lookup state={credentials}>
                  {!credentials.data?.length ? (
                    <Alert variant="warning">
                      {t('pages:onboarding.credential.noCredentials')}
                    </Alert>
                  ) : (
                    <Field id="vm-credential" label={t('pages:onboarding.credential.title')}>
                      <fieldset disabled={locked || reapply || validating}>
                        <Choice
                          id="vm-credential"
                          value={draft.credentialId}
                          onChange={(id) => {
                            patch('credentialId', id)
                            setValidatedCredential('')
                            setValidationError(false)
                          }}
                        >
                          <SelectItem value="__none">
                            {t('pages:onboarding.credential.select')}
                          </SelectItem>
                          {credentials.data.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.name}
                            </SelectItem>
                          ))}
                        </Choice>
                      </fieldset>
                    </Field>
                  )}
                </Lookup>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" asChild>
                    <Link to="/credentials" target="_blank" rel="noopener noreferrer">
                      {t('pages:vmWizard.manageCredentialsNewTab', 'Manage credentials (new tab)')}
                    </Link>
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      credentials.retry()
                      setValidatedCredential('')
                    }}
                  >
                    {t('pages:vmWizard.refreshCredentials', 'Refresh credentials')}
                  </Button>
                </div>
                {credential && (
                  <>
                    <div className="rounded-lg bg-slate-50 p-4 dark:bg-slate-800">
                      <p className="text-sm text-slate-500">
                        {provider === 'azure'
                          ? t('pages:vmWizard.subscription', 'Azure subscription')
                          : t('pages:vmWizard.destination', 'Deployment destination')}
                      </p>
                      <p className="break-all font-mono text-sm">
                        {destination ||
                          (provider === 'upcloud'
                            ? credential.name
                            : t(
                                'pages:vmWizard.destinationMissing',
                                'Missing destination metadata. Update the credential before continuing.'
                              ))}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      disabled={validating}
                      onClick={() => void checkCredential()}
                    >
                      {validating ? <Spinner /> : null}
                      {t('pages:vmWizard.validateCredential', 'Validate connection')}
                    </Button>
                    {validationError && (
                      <Alert variant="error">
                        {t(
                          'pages:vmWizard.validationFailed',
                          'Connection validation failed. Check the credential and permissions, then try again.'
                        )}
                      </Alert>
                    )}
                    {connectionValid && (
                      <Alert variant="success">
                        {t(
                          'pages:vmWizard.connectionVerified',
                          'Credential validated. Azure quota, network reachability and provisioning permissions still require cloud preflight checks.'
                        )}
                      </Alert>
                    )}
                  </>
                )}
              </Section>
            )}
            {draft && step === 2 && (
              <fieldset disabled={locked || busy} className="min-w-0 space-y-6">
                <Section title={t('pages:vmWizard.compute', 'Compute')}>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field
                      id="vm-region"
                      label={t('pages:onboarding.configure.region')}
                      hint={t(
                        'pages:vmWizard.regionHint',
                        'Use the cloud region identifier. Verify capacity and image availability in this region.'
                      )}
                    >
                      <Input
                        id="vm-region"
                        disabled={reapply}
                        value={draft.region}
                        onChange={(e) => patch('region', e.target.value)}
                      />
                    </Field>
                    <Field id="vm-size" label={t('pages:onboarding.configure.vmSize')}>
                      <EditableCombobox
                        id="vm-size"
                        value={draft.vmSize}
                        onValueChange={chooseVmSize}
                        options={
                          provider === 'azure'
                            ? AZURE_VM_SIZES
                            : provider === 'upcloud'
                              ? UPCLOUD_PLANS
                              : gcpVmSizesForRegion(draft.region)
                        }
                      />
                    </Field>
                  </div>
                  <Label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={draft.jinaRerankerEnabled && canEnableJinaReranker(draft.vmSize)}
                      disabled={!canEnableJinaReranker(draft.vmSize)}
                      onCheckedChange={(v) => patch('jinaRerankerEnabled', v === true)}
                    />
                    {t('pages:vmWizard.reranker', 'Enable local reranker')}
                  </Label>
                </Section>
                {provider !== 'upcloud' && (
                  <Section title={t('pages:vmWizard.network', 'Network')}>
                    <p className="text-sm text-slate-500">
                      {t(
                        'pages:vmWizard.networkHint',
                        'Choose ranges that do not overlap your connected networks, including ExpressRoute. These checks validate the local allocation, not your remote network topology.'
                      )}
                    </p>
                    <div className="grid gap-4 sm:grid-cols-2">
                      {networkKeys.map((key) => (
                        <Field key={key} id={`vm-${key}`} label={labels[key]}>
                          <Input
                            id={`vm-${key}`}
                            value={draft[key]}
                            onChange={(e) => patch(key, e.target.value)}
                          />
                        </Field>
                      ))}
                    </div>
                    {provider === 'azure' && (
                      <>
                        <Button
                          variant="outline"
                          disabled={!suggestAzureNetwork(draft.networkCidr)}
                          onClick={() => {
                            const network = suggestAzureNetwork(draft.networkCidr)
                            if (network) setDraft({ ...draft, ...network })
                          }}
                        >
                          {t('pages:vmWizard.suggestSubnets', 'Suggest subnets inside this VNet')}
                        </Button>
                        {!validAzureNetwork(draft) && (
                          <Alert variant="error">
                            {t(
                              'pages:vmWizard.invalidNetwork',
                              'Enter aligned IPv4 ranges. All subnets must fit inside the VNet, be /29 or larger, and must not overlap.'
                            )}
                          </Alert>
                        )}
                      </>
                    )}
                  </Section>
                )}
                <Section title={t('pages:vmWizard.release', 'Terraform release')}>
                  <Lookup
                    state={modules}
                    errorMessage={t('pages:vmWizard.releasesFailed', 'Could not load Terraform releases. Check the infra-service registry connection, then retry. Your selections have been kept.')}
                    retryLabel={t('pages:vmWizard.retryReleases', 'Retry releases')}
                  >
                    {!versions.length ? (
                      <Alert variant="error">
                        {t(
                          'pages:vmWizard.noRelease',
                          'No pinned releases are available for this provider. Configure the registry connection or publish a release before provisioning.'
                        )}
                      </Alert>
                    ) : (
                      <Field
                        id="vm-release"
                        label={t('pages:onboarding.configure.tfModuleVersion')}
                      >
                        <Choice
                          id="vm-release"
                          value={draft.moduleVersion}
                          onChange={chooseVersion}
                        >
                          <SelectItem value="__none">
                            {t('pages:vmWizard.selectRelease', 'Select a pinned release')}
                          </SelectItem>
                          {versions.map((v) => (
                            <SelectItem key={v} value={v}>
                              {v}
                            </SelectItem>
                          ))}
                        </Choice>
                      </Field>
                    )}
                    <Lookup
                      state={releaseLookup}
                      errorMessage={t('pages:vmWizard.selectedReleaseFailed', 'Could not resolve the selected Terraform release from the registry. Your selection has been kept.')}
                      retryLabel={t('pages:vmWizard.retryRelease', 'Retry selected release')}
                    >
                      {release && (
                        <p className="break-all text-xs text-slate-500">{release.digest}</p>
                      )}
                      {release && (
                        <p className="text-sm text-slate-500">
                          {t('pages:vmWizard.engine', 'Terraform engine')}:{' '}
                          {release.terraformVersion ||
                            t(
                              'pages:vmWizard.engineUnknown',
                              'Not reported by this artifact; the immutable container supplies the engine.'
                            )}
                        </p>
                      )}
                      {release && draft.moduleDigest !== release.digest && (
                        <Alert variant="error">
                          {t(
                            'pages:vmWizard.releaseChanged',
                            'The release tag changed after selection. Refresh the catalog and explicitly select the release again.'
                          )}
                        </Alert>
                      )}
                    </Lookup>
                  </Lookup>
                </Section>
                <Section title={t('pages:vmWizard.baseImage', 'VM base image')}>
                  <Lookup
                    state={images}
                    errorMessage={provider === 'azure'
                      ? t('pages:vmWizard.galleryFailed', 'Could not load VM base images from Agion’s public Azure gallery. Check the Azure connection and selected region, then retry.')
                      : t('pages:vmWizard.imagesFailed', 'Could not load VM base images. Retry to check which images are available. Your selection has been kept.')}
                    retryLabel={t('pages:vmWizard.retryImages', 'Retry images')}
                  >
                    {provider === 'azure' && (
                      <p className="break-words text-sm text-slate-500">
                        {t('pages:vmWizard.gallerySource', 'Source: Agion public Azure gallery · {{region}}', { region: imageRegion })}
                        {images.data?.gallery && <span className="mt-1 block text-xs">{images.data.gallery}</span>}
                      </p>
                    )}
                    {!images.data?.images.length ? (
                      <Alert variant="warning">
                        {provider === 'azure'
                          ? t('pages:vmWizard.galleryEmpty', 'No supported images are published in Agion’s public Azure gallery for this region. Choose another region or ask the image publisher to check replication.')
                          : t('pages:onboarding.configure.noPackerImages')}
                      </Alert>
                    ) : (
                      <Field id="vm-image" label={t('pages:onboarding.configure.packerImage')}>
                        <Choice
                          id="vm-image"
                          value={draft.imageName}
                          onChange={(name) => patch('imageName', name)}
                        >
                          <SelectItem value="__none">
                            {t('pages:onboarding.configure.packerImagePlaceholder')}
                          </SelectItem>
                          {images.data.images.map((i) => (
                            <SelectItem
                              key={i.id}
                              value={vmImageReference(provider, i)}
                            >{`${i.name} ${i.version}`}</SelectItem>
                          ))}
                        </Choice>
                      </Field>
                    )}
                  </Lookup>
                  {reapply && provider === 'upcloud' && draft.imageName !== reopened?.imageName && (
                    <Alert variant="warning">
                      {t(
                        'pages:vmWizard.imageRebuildsUpcloud',
                        'A different base image rebuilds the server. Everything on its local disk is erased, including host credentials and logs. Keep the current image to change only the plan, disk or storage settings.'
                      )}
                    </Alert>
                  )}
                  {reapply && provider === 'gcp' && draft.imageName !== reopened?.imageName && (
                    <Alert variant="error">
                      {t(
                        'pages:vmWizard.imageRebuildsGcp',
                        'A different image destroys this virtual machine and its disk, then builds a new one from that image. Everything on the disk is gone: the local database, the host credentials and the logs. Keep the current image to change only the machine type, disk or storage settings.'
                      )}
                    </Alert>
                  )}
                  <div className="flex flex-wrap items-center gap-3">
                    {provider !== 'azure' && <Button asChild variant="outline">
                      <Link to="/images" target="_blank" rel="noopener noreferrer">
                        {t('pages:vmWizard.manageImages', 'Manage VM images')}
                      </Link>
                    </Button>}
                    {!images.error && (
                      <Button variant="outline" disabled={images.loading} onClick={images.retry}>
                        {t('pages:vmWizard.refreshImages', 'Refresh images')}
                      </Button>
                    )}
                  </div>
                </Section>
                <Section title={t('pages:vmWizard.access', 'Host access')}>
                  <fieldset disabled={reapply}>
                  <Lookup state={keys}>
                    <Field id="vm-ssh" label={t('pages:onboarding.configure.sshKey')}>
                      <Choice
                        id="vm-ssh"
                        value={draft.sshKeyId}
                        onChange={(id) => patch('sshKeyId', id)}
                      >
                        <SelectItem value="__none">
                          {t('pages:vmWizard.noSshKey', 'No additional SSH key')}
                        </SelectItem>
                        {keys.data?.map((k) => (
                          <SelectItem key={k.id} value={k.id}>
                            {k.name}
                          </SelectItem>
                        ))}
                      </Choice>
                    </Field>
                  </Lookup>
                  </fieldset>
                  {provider === 'azure' && (
                    <AzureSshAccess mode={draft.sshAccessMode} sources={draft.sshAllowedCidrs}
                      onModeChange={(mode) => patch('sshAccessMode', mode)}
                      onSourcesChange={(sources) => patch('sshAllowedCidrs', sources)} />
                  )}
                </Section>
                <Section title={t('pages:vmWizard.database', 'Database')}>
                  <Label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={draft.managedDbEnabled}
                      onCheckedChange={(v) => patch('managedDbEnabled', v === true)}
                    />
                    {t('pages:onboarding.configure.managedDb')}
                  </Label>
                  {!draft.managedDbEnabled && (
                    <p className="text-sm text-slate-500">
                      {t(
                        'pages:vmWizard.localDb',
                        'Database runs on the VM. This is a single-host deployment, not a highly available database.'
                      )}
                    </p>
                  )}
                  {draft.managedDbEnabled && (
                    <>
                      <Field
                        id="vm-db-tier"
                        label={t('pages:onboarding.configure.dbTier', 'Database tier')}
                      >
                        <Choice
                          id="vm-db-tier"
                          value={draft.dbTier}
                          onChange={(v) => patch('dbTier', v)}
                        >
                          {databaseTiers.map((v) => (
                            <SelectItem key={v.value} value={v.value}>
                              {v.label}
                            </SelectItem>
                          ))}
                        </Choice>
                      </Field>
                      {provider === 'gcp' && (
                        <Field id="vm-db-disk" label={t('pages:vmWizard.databaseDisk')}
                          hint={t('pages:vmWizard.databaseDiskHint')}>
                          <Input id="vm-db-disk" type="number" min={1} step={1}
                            value={draft.dbDiskSizeGb}
                            onChange={(event) => patch('dbDiskSizeGb', event.target.value)} />
                        </Field>
                      )}
                      <Label className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={draft.separateIntelligenceDb}
                          onCheckedChange={(v) => patch('separateIntelligenceDb', v === true)}
                        />
                        {t('pages:onboarding.configure.separateIntelligenceDb', 'Separate intelligence database')}
                      </Label>
                      {draft.separateIntelligenceDb && (
                        <Field
                          id="vm-intelligence-db-tier"
                          label={t(
                            'pages:vmWizard.intelligenceDbTier',
                            'Intelligence database tier'
                          )}
                        >
                          <Choice
                            id="vm-intelligence-db-tier"
                            value={draft.intelligenceDbTier}
                            onChange={(v) => patch('intelligenceDbTier', v)}
                          >
                            <SelectItem value="__none">
                              {t('pages:onboarding.configure.dbTierPlaceholder', 'Select a database tier')}
                            </SelectItem>
                            {databaseTiers.map((v) => (
                              <SelectItem key={v.value} value={v.value}>
                                {v.label}
                              </SelectItem>
                            ))}
                          </Choice>
                        </Field>
                      )}
                    </>
                  )}
                </Section>
                <Section
                  title={t('pages:vmWizard.storageObservability', 'Storage and observability')}
                >
                  {provider === 'azure' ? (
                    <AdvancedSettings
                      provider="azure"
                      hideAccess
                      region={draft.region}
                      open={advanced}
                      onOpenChange={setAdvanced}
                      availabilityZones={draft.availabilityZones}
                      onAvailabilityZonesChange={(v) => patch('availabilityZones', v)}
                      reapply={reapply}
                      appliedDiskGb={appliedDisk}
                      diskSizeGb={draft.diskSizeGb}
                      onDiskSizeChange={(v) => patch('diskSizeGb', v)}
                      dfsEnabled={draft.dfsEnabled}
                      onDfsEnabledChange={(v) => patch('dfsEnabled', v)}
                      dfsReplication={draft.dfsReplication}
                      onDfsReplicationChange={(v) => patch('dfsReplication', v)}
                      diagnosticsEnabled={draft.diagnosticsEnabled}
                      onDiagnosticsEnabledChange={(v) => patch('diagnosticsEnabled', v)}
                      logRetentionDays={draft.logRetentionDays}
                      onLogRetentionDaysChange={(v) => patch('logRetentionDays', v)}
                      sshAllowedCidrs={draft.sshAllowedCidrs}
                      onSshAllowedCidrsChange={(v) => patch('sshAllowedCidrs', v)}
                      t={t}
                    />
                  ) : provider === 'gcp' ? (
                    <AdvancedSettings
                      provider="gcp"
                      region={draft.region}
                      open={advanced}
                      onOpenChange={setAdvanced}
                      zone={draft.zone}
                      onZoneChange={(v) => patch('zone', v)}
                      reapply={reapply}
                      appliedDiskGb={appliedDisk}
                      diskSizeGb={draft.diskSizeGb}
                      onDiskSizeChange={(v) => patch('diskSizeGb', v)}
                      dfsEnabled={draft.dfsEnabled}
                      onDfsEnabledChange={(v) => patch('dfsEnabled', v)}
                      t={t}
                    />
                  ) : (
                    <AdvancedSettings
                      provider="upcloud"
                      region={draft.region}
                      open={advanced}
                      onOpenChange={setAdvanced}
                      includedStorageGb={upcloudPlanStorageGb(draft.vmSize)}
                      reapply={reapply}
                      appliedDiskGb={appliedDisk}
                      diskSizeGb={draft.diskSizeGb}
                      onDiskSizeChange={chooseDiskSize}
                      dfsEnabled={draft.dfsEnabled}
                      onDfsEnabledChange={(v) => patch('dfsEnabled', v)}
                      t={t}
                    />
                  )}
                </Section>
              </fieldset>
            )}
            {environment && step === 2 && (
              <Button
                variant="outline"
                onClick={() => {
                  modules.retry()
                  images.retry()
                  keys.retry()
                  releaseLookup.retry()
                }}
              >
                {t('pages:vmWizard.refreshConfiguration', 'Refresh configuration lookups')}
              </Button>
            )}
            {draft && step === 3 && (
              <div className="space-y-5">
                <Alert variant="info">
                  {reapply ? t('pages:vmWizard.reapplyReview') : t(
                    'pages:vmWizard.reviewHint',
                    'This is a configuration review, not a Terraform plan. Provisioning creates billable resources in the selected subscription. No cloud resources have been created by this wizard yet.'
                  )}
                </Alert>
                <dl className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-x-4 gap-y-3 text-sm">
                  {[
                    [t('pages:vmWizard.organization', 'Organization'), organization.name],
                    [t('pages:vmWizard.target', 'Target'), targetName],
                    [
                      t('pages:vmWizard.destination', 'Deployment destination'),
                      destination || credential?.name,
                    ],
                    [t('pages:onboarding.configure.region'), draft.region],
                    [t('pages:onboarding.configure.vmSize'), draft.vmSize],
                    [t('pages:vmWizard.release', 'Deployment release'), releaseLabel],
                    [t('pages:onboarding.configure.packerImage'), draft.imageName],
                    ...(provider === 'azure'
                      ? Object.entries(labels).map(([key, label]) => [
                          label,
                          draft[key as keyof typeof labels],
                        ])
                      : []),
                    [
                      t('pages:vmWizard.database', 'Database'),
                      draft.managedDbEnabled ? draft.dbTier : t('pages:vmWizard.onVm', 'On VM'),
                    ],
                    ...(provider === 'gcp' && draft.managedDbEnabled ? [[
                      t('pages:vmWizard.databaseDisk'),
                      draft.dbDiskSizeGb || (reapply ? t('pages:vmWizard.databaseDiskPreserve') : '20'),
                    ]] : []),
                    [
                      t('pages:vmWizard.storageObservability', 'Storage and observability'),
                      `${t('pages:vmWizard.dfs', 'File storage')}: ${draft.dfsEnabled ? t('pages:vmWizard.enabled', 'Enabled') : t('pages:vmWizard.disabled', 'Disabled')}`,
                    ],
                  ].map(([label, value]) => (
                    <div className="contents" key={label}>
                      <dt className="break-words text-slate-500">{label}</dt>
                      <dd className="mb-2 break-words font-medium sm:mb-0">{value}</dd>
                    </div>
                  ))}
                </dl>
                <dl className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-x-4 gap-y-3 text-sm">
                  {[
                    [
                      t('pages:onboarding.configure.sshKey'),
                      keys.data?.find((k) => k.id === draft.sshKeyId)?.name ||
                        t('pages:vmWizard.noSshKey', 'No additional SSH key'),
                    ],
                    [
                      t('pages:onboarding.configure.diskSize', 'Boot disk size (GB)'),
                      // An empty field is not "unchanged" everywhere. UpCloud
                      // resolves it live, gcp-vm and azure-vm resend the
                      // recorded size, and with nothing recorded both fall to
                      // the module default of 50 GB — which rebuilds a gcp-vm
                      // around a smaller disk. Name the size that will apply.
                      draft.diskSizeGb || (emptyDiskKeepsCurrent(provider, reapply, appliedDisk)
                        ? appliedDisk !== undefined && provider !== 'upcloud'
                          ? String(appliedDisk)
                          : t('pages:vmWizard.diskUnchanged', 'Current size (unchanged)')
                        : '50'),
                    ],
                    [
                      t('pages:vmWizard.reranker', 'Enable local reranker'),
                      draft.jinaRerankerEnabled && canEnableJinaReranker(draft.vmSize)
                        ? t('pages:vmWizard.enabled', 'Enabled')
                        : t('pages:vmWizard.disabled', 'Disabled'),
                    ],
                    ...(draft.managedDbEnabled && draft.separateIntelligenceDb
                      ? [
                          [
                            t('pages:vmWizard.intelligenceDbTier', 'Intelligence database tier'),
                            draft.intelligenceDbTier,
                          ],
                        ]
                      : []),
                    ...(provider === 'azure'
                      ? [
                          [
                            t('pages:sshAccess.reviewLabel'),
                            draft.sshAccessMode === 'vpn_only'
                              ? t('pages:sshAccess.vpnOnly')
                              : `${t(draft.sshAccessMode === 'legacy' ? 'pages:sshAccess.legacy' : 'pages:sshAccess.privateNetwork')}: ${draft.sshAllowedCidrs || String.fromCharCode(42)}`,
                          ],
                          [
                            t('pages:onboarding.configure.availabilityZone', 'Availability Zone'),
                            draft.availabilityZones.join(', ') ||
                              t(
                                'pages:onboarding.configure.availabilityZoneDefault',
                                'Automatic (regional)'
                              ),
                          ],
                          [
                            t(
                              'pages:onboarding.configure.dfsReplication',
                              'DFS storage replication'
                            ),
                            draft.dfsEnabled
                              ? draft.dfsReplication
                              : t('pages:vmWizard.disabled', 'Disabled'),
                          ],
                          [
                            t('pages:vmWizard.diagnostics', 'Diagnostics'),
                            draft.diagnosticsEnabled
                              ? t('pages:vmWizard.enabled', 'Enabled')
                              : t('pages:vmWizard.disabled', 'Disabled'),
                          ],
                          ...(draft.diagnosticsEnabled
                            ? [
                                [
                                  t(
                                    'pages:onboarding.configure.logRetention',
                                    'Log retention (days)'
                                  ),
                                  draft.logRetentionDays,
                                ],
                              ]
                            : []),
                        ]
                      : provider === 'gcp'
                        ? [
                            [t('pages:vmWizard.vnet', 'VNet address space'), draft.networkCidr],
                            [
                              t('pages:onboarding.configure.zone', 'Zone'),
                              draft.zone ||
                                t('pages:vmWizard.defaultZone', {
                                  defaultValue: '{{region}}-b',
                                  region: draft.region,
                                }),
                            ],
                          ]
                        : []),
                  ].map(([label, value]) => (
                    <div className="contents" key={label}>
                      <dt className="break-words text-slate-500">{label}</dt>
                      <dd className="mb-2 break-words font-medium sm:mb-0">{value}</dd>
                    </div>
                  ))}
                </dl>
                <p className="break-all font-mono text-xs text-slate-500">{draft.moduleDigest}</p>
                <p className="text-sm">
                  {t('pages:vmWizard.engine', 'Terraform engine')}:{' '}
                  {draft.terraformVersion ||
                    t(
                      'pages:vmWizard.engineUnknown',
                      'Not reported by this artifact; the immutable container supplies the engine.'
                    )}
                </p>
                {draft.sshAccessMode === 'legacy' && publicSsh && (
                  <Alert variant="warning">
                    {t(
                      'pages:vmWizard.publicSsh',
                      'SSH is open to all source addresses. Restrict it to your administrative networks before provisioning unless this exposure is intentional.'
                    )}
                  </Alert>
                )}
                <Alert variant="warning">
                  {t(
                    'pages:vmWizard.singleVmWarning',
                    'One VM means one failure domain. Storage replication does not make the VM highly available.'
                  )}
                </Alert>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 p-5 dark:border-slate-700">
            <Button
              variant="outline"
              disabled={step === (reapply ? 1 : 0) || busy}
              onClick={() => setStep((s) => s - 1)}
            >
              <ArrowLeft size={16} />
              {t('pages:onboarding.back')}
            </Button>
            {step < 3 ? (
              <Button disabled={!nextAllowed || busy} onClick={() => setStep((s) => s + 1)}>
                {t('pages:onboarding.next')}
                <ArrowRight size={16} />
              </Button>
            ) : (
              <Button
                disabled={busy || !allReady || !configValid || !connectionValid}
                onClick={() => void submit()}
              >
                {busy ? <Spinner /> : <Rocket size={16} />}
                {reapply && !submissionStarted ? t('pages:vmWizard.apply') : environment
                  ? t('pages:vmWizard.resumeSubmission', 'Resume submission')
                  : t('pages:vmWizard.provision', 'Provision VM')}
              </Button>
            )}
            {!nextAllowed && step === 2 && (
              <div className="w-full rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100" role="status" aria-live="polite">
                <p className="font-medium">{t('pages:vmWizard.requirementsTitle', 'Before you continue')}</p>
                <ul className="mt-2 list-disc space-y-1 pl-5">
                  {[...configurationErrors].map((issue) => (
                    <li key={issue}>{configurationErrorMessages[issue]}</li>
                  ))}
                  {configurationLookups.filter(({ state }) => state.loading || state.error).map(({ state, label }) => (
                    <li key={label}>
                      {state.loading
                        ? t('pages:vmWizard.requirementLoading', 'Waiting for {{section}} to load.', { section: label })
                        : t('pages:vmWizard.requirementFailed', 'Retry the failed lookup in {{section}}.', { section: label })}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!nextAllowed && step < 2 && (
              <p className="w-full text-sm text-slate-500" role="status">
                {t(
                  'pages:vmWizard.completeStep',
                  'Complete this section and resolve any loading errors to continue.'
                )}
              </p>
            )}
          </div>
        </Card>
        <aside
          className="space-y-3 rounded-xl border border-slate-200 bg-white p-5 text-sm lg:sticky lg:top-6 dark:border-slate-700 dark:bg-slate-900"
          aria-label={t('pages:vmWizard.summary', 'Deployment summary')}
        >
          <h2 className="font-semibold">{t('pages:vmWizard.summary', 'Deployment summary')}</h2>
          <p>{organization.name}</p>
          <p className="break-all text-xs text-slate-500">{organization.id}</p>
          {targetName && (
            <>
              <hr className="border-slate-200 dark:border-slate-700" />
              <p className="font-medium">{targetName}</p>
              <p className="uppercase">{provider}</p>
              <p>{draft?.region}</p>
              <p>{draft?.vmSize}</p>
            </>
          )}
          <p className="text-slate-500">
            {reapply ? t('pages:vmWizard.reapplyNoChanges') : t(
              'pages:vmWizard.noChangesBeforeConfirm',
              'Configuration and review do not run Terraform. Provision VM is the cloud-changing action.'
            )}
          </p>
        </aside>
      </div>
    </div>
  )
}
